import { Fragment, useCallback, useMemo } from 'react';

import { Tooltip, makeStyles } from '@material-ui/core';

import classNames from 'classnames';

import {
  Button,
  MenuAutocompleteListbox,
  MenuListBoxItem,
  MenuTrigger,
} from '@backstage/ui';

import { CountryUtils } from '../../utils';

const FLAG_STACK_THRESHOLD = 3;
const MAX_FLAGS_DISPLAYED = 8;

const useStyles = makeStyles({
  button: {
    // @ts-expect-error - MUI makeStyles types don't support CSS custom properties
    fontWeight: 'var(--bui-font-weight-regular)',
    textTransform: 'none',
    minWidth: 'auto',
    padding: 0,
    height: 'auto',
    '&:hover, &:active, &:focus': {
      backgroundColor: 'transparent',
      opacity: 1,
    },
  },
  readOnly: {
    opacity: 1,
    cursor: 'default',
  },
  flagGroup: {
    display: 'inline-flex',
    alignItems: 'center',
    marginRight: 'var(--bui-space-1)',
  },
  flag: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    lineHeight: 1,
    marginLeft: '-4px',
    '&:first-child': {
      marginLeft: 0,
    },
  },
});

const isValidCountryCode = (v: unknown): v is string =>
  typeof v === 'string' && CountryUtils.countryCodes.includes(v);

export interface CountryAutocompleteProps {
  value: string | string[] | null;
  onChange: (value: string | string[] | null) => void;
  multiple?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
}

export const CountryAutocomplete = ({
  value,
  onChange,
  multiple = false,
  disabled = false,
  readOnly = false,
}: CountryAutocompleteProps) => {
  const classes = useStyles();

  const selectedValues = useMemo(() => {
    let values: unknown[];
    if (Array.isArray(value)) {
      values = value;
    } else if (value) {
      values = [value];
    } else {
      values = [];
    }
    return values.filter(isValidCountryCode);
  }, [value]);

  const selectedKeys = useMemo(() => new Set(selectedValues), [selectedValues]);

  const displayLabel = useMemo(() => {
    if (selectedValues.length === 0) {
      return 'Select...';
    }

    if (multiple && selectedValues.length > FLAG_STACK_THRESHOLD) {
      const flagsToShow = selectedValues.slice(0, MAX_FLAGS_DISPLAYED);
      return (
        <Fragment>
          <span className={classes.flagGroup}>
            {flagsToShow.map(code => (
              <span key={code} className={classes.flag}>
                {CountryUtils.getCountryFlag(code)}
              </span>
            ))}
          </span>
          {selectedValues.length} countries
        </Fragment>
      );
    }

    return selectedValues.map(CountryUtils.getCountryLabel).join(', ');
  }, [selectedValues, multiple, classes.flagGroup, classes.flag]);

  const handleSelectionChange = useCallback(
    (keys: 'all' | Set<React.Key>) => {
      if (keys === 'all') {
        const allCodes = [...CountryUtils.countryCodes];
        onChange(multiple ? allCodes : allCodes[0] || null);
        return;
      }

      const newValues = Array.from(keys) as string[];
      onChange(multiple ? newValues : newValues[0] || null);
    },
    [multiple, onChange],
  );

  const tooltipText = selectedValues
    .map(CountryUtils.getCountryLabel)
    .join(', ');

  const triggerButton = (
    <Button
      variant="tertiary"
      size="small"
      className={classNames(classes.button, { [classes.readOnly]: readOnly })}
      isDisabled={disabled || readOnly}
    >
      {displayLabel}
    </Button>
  );

  if (readOnly) {
    return (
      <Tooltip title={tooltipText} placement="top" arrow>
        <span>{triggerButton}</span>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tooltipText} placement="top" arrow>
      <span>
        <MenuTrigger>
          {triggerButton}
          <MenuAutocompleteListbox
            aria-label="Select country"
            selectionMode={multiple ? 'multiple' : 'single'}
            selectedKeys={selectedKeys}
            onSelectionChange={handleSelectionChange}
          >
            {CountryUtils.countryCodes.map(code => (
              <MenuListBoxItem
                key={code}
                id={code}
                textValue={CountryUtils.getCountryLabel(code)}
              >
                {CountryUtils.getCountryLabel(code)}
              </MenuListBoxItem>
            ))}
          </MenuAutocompleteListbox>
        </MenuTrigger>
      </span>
    </Tooltip>
  );
};
